/**
 * Shared risk level utilities.
 *
 * The well-known risk level codes are captured in the `RiskLevelCode` union
 * type.  This keeps Customer interfaces type-safe while remaining compatible
 * with dynamic data from the database.  At API boundaries (mappers) we cast
 * the raw `string` coming from the backend to `RiskLevelCode`.
 *
 * The helpers below provide consistent styling for the well-known codes
 * while gracefully falling back for any unknown value.
 */

/** Well-known risk level codes (lowercase, as returned by the customer API). */
export type RiskLevelCode = "critical" | "high" | "medium" | "low";

/** Badge class names keyed by lowercase risk code (for <Badge className={…}> usage). */
export const getRiskBadgeClassName = (risk: RiskLevelCode): string => {
  switch (risk.toLowerCase()) {
    case "critical":
      return "bg-red-900 text-white hover:bg-red-950";
    case "high":
      return "bg-red-500 text-white hover:bg-red-600";
    case "medium":
      return "bg-amber-500 text-white hover:bg-amber-600";
    case "low":
      return "bg-[#14b8a6] text-white hover:bg-[#0d9488]";
    default:
      return "bg-muted text-muted-foreground";
  }
};

/** Text color class names keyed by lowercase risk code. */
export const getRiskTextClassName = (risk: RiskLevelCode): string => {
  switch (risk.toLowerCase()) {
    case "critical":
      return "text-red-900 dark:text-red-400";
    case "high":
      return "text-red-600 dark:text-red-500";
    case "medium":
      return "text-amber-600 dark:text-amber-500";
    case "low":
      return "text-green-600 dark:text-green-500";
    default:
      return "text-muted-foreground";
  }
};

/** Human-readable label for a risk code (e.g. "high" → "High Risk"). */
export const getRiskLabel = (risk: RiskLevelCode): string => {
  const code = risk.toLowerCase();
  switch (code) {
    case "critical":
      return "Critical Risk";
    case "high":
      return "High Risk";
    case "medium":
      return "Medium Risk";
    case "low":
      return "Low Risk";
    default:
      return risk ? `${risk} Risk` : "Unknown Risk";
  }
};
